import { useState } from 'react';
import { Box, Link, Paper, Typography } from "@mui/material";
import { Localized } from "@fluent/react";
import { ImportedDocument } from "./importedDocumentsApi";
import { DocumentKeyValueRow } from "../utils/DocumentKeyValueRow";
import { EditDocumentNumberDialog } from '../components/EditDocumentNumberDialog';
import { EditDocumentLanguageDialog } from '../components/EditDocumentLanguageDialog';
import { EditDocumentStructureDialog } from '../components/EditDocumentStructureDialog';
import { EditDescriptionField } from '../components/EditDescriptionField';
import { DocumentDescriptionField } from '../documentsApi';
import { DocumentLanguages } from '../documentTypes';
import { useAppDispatch } from '../../../app/hooks';
import { updateImportedDocumentHeader } from './importedDocSlice';
import { StructureName } from '../../device/Structure';
import { ImportedDocumentCounterpartyCard } from './components/ImportedDocumentCounterpartyCard';
import { CopyToClipboardButton } from '../../../app/components/CopyToClipboardButton';
import { AssessmentSummaryCard } from './components/AssessmentSummaryCard';

type DialogState
    = "none"
    | "editDocumentNumber"
    | "editDocumentLanguage"
    | "editDocumentStructure"
    | { tag: 'editDescriptionField', field: DocumentDescriptionField, value: string }

export const ImportedDocumentDetailsPanel = ({ document }: { document: ImportedDocument }) => {
    const dispatch = useAppDispatch()
    const [dialog, setDialog] = useState<DialogState>("none")
    const [documentNumber, setDocumentNumber] = useState(document.number);
    const [title, setTitle] = useState(document.title);
    const [language, setLanguage] = useState<DocumentLanguages>(document.language as DocumentLanguages);
    const [structureId, setStructureId] = useState(document.structureId);

    const assessmentUrl = `${window.location.origin}/imported-documents/assess/${document.id}`;

    const editDocumentNumberSuccess = (newValue: string) => {
        setDialog("none")
        setDocumentNumber(newValue)
        dispatch(updateImportedDocumentHeader({ number: newValue }))
    }

    const editDocumentLanguageSuccess = (newValue: DocumentLanguages) => {
        setDialog("none")
        setLanguage(newValue)
        dispatch(updateImportedDocumentHeader({ language: newValue }))
    }

    const editDescriptionFieldSuccess = (fieldName: DocumentDescriptionField, newValue: string) => {
        setDialog("none")
        if (fieldName === 'title') {
            setTitle(newValue)
            dispatch(updateImportedDocumentHeader({ title: newValue }))
        }
    }

    const editDocumentStructureSuccess = (newValue: string) => {
        setDialog("none")
        setStructureId(newValue)
        dispatch(updateImportedDocumentHeader({ structureId: newValue }))
    }

    return (
        <>
            <Paper elevation={3} sx={{ p: 1, mb: 1 }}>
                <Box>
                    <DocumentKeyValueRow
                        label={<Localized id="document-number-long">_number</Localized>}
                        value={documentNumber || "-"}
                        editable
                        onEdit={() => setDialog("editDocumentNumber")}
                    />

                    <DocumentKeyValueRow
                        label={<Localized id="document-title">_title</Localized>}
                        value={title || "-"}
                        editable
                        onEdit={() => setDialog({ tag: 'editDescriptionField', field: 'title', value: title })}
                    />

                    <DocumentKeyValueRow
                        label={<Localized id="document-language">_document-language</Localized>}
                        value={language || "-"}
                        editable
                        onEdit={() => setDialog("editDocumentLanguage")}
                    />
                    
                    <DocumentKeyValueRow
                        label={<Localized id="structure">_structure</Localized>}
                        value={structureId ? <StructureName structureId={String(structureId)} /> : "-"}
                        editable
                        onEdit={() => setDialog("editDocumentStructure")}
                    />
                </Box>
            </Paper>

            <Paper elevation={3} sx={{ p: 1, mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                    <Localized id="imported-doc-assessment-link">_Assessment link</Localized>
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    <Localized id="imported-doc-assessment-link-hint">
                        _Share this link with department users to let them comment on assigned fragments.
                    </Localized>
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Link
                        href={assessmentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ wordBreak: 'break-all' }}
                    >
                        {assessmentUrl}
                    </Link>
                    <CopyToClipboardButton value={assessmentUrl} />
                </Box>
            </Paper>

            <AssessmentSummaryCard docId={document.id} docGuid={document.guid} elements={document.elements} />

            <ImportedDocumentCounterpartyCard document={document} />


            {dialog === 'editDocumentNumber' && (
                <EditDocumentNumberDialog
                    documentId={String(document.id)}
                    value={documentNumber}
                    onSuccess={editDocumentNumberSuccess}
                    onCancel={() => setDialog('none')}
                />
            )}
            {dialog === 'editDocumentLanguage' && (
                <EditDocumentLanguageDialog
                    documentId={String(document.id)}
                    defLanguage={language}
                    onSuccess={editDocumentLanguageSuccess}
                    onCancel={() => setDialog("none")}
                />
            )}
            {dialog === 'editDocumentStructure' && (
                <EditDocumentStructureDialog
                    documentId={String(document.id)}
                    defStructureId={structureId}
                    onSuccess={editDocumentStructureSuccess}
                    onCancel={() => setDialog("none")}
                />
            )}
            {typeof dialog === 'object' && dialog.tag === 'editDescriptionField' && (
                <EditDescriptionField
                    documentId={String(document.id)}
                    fieldName={dialog.field}
                    value={dialog.value}
                    onSuccess={editDescriptionFieldSuccess}
                    onCancel={() => setDialog("none")}
                />
            )}
        </>
    );
};
